import { SelectButton } from 'primereact/selectbutton';
import React, { memo, useCallback, useMemo } from 'react';

import './TlOnCameraSelect.scss';

export type TlOnCameraValue = 'Yes' | 'No' | 'NA';

interface TlOnCameraOption {
    label: string;
    value: TlOnCameraValue;
}

interface TlOnCameraSelectProps {
    /** Question text rendered above the button group */
    label: string;
    /** Currently selected value — pass null when unset */
    value: TlOnCameraValue | null;
    /** Fires with the new selection whenever the user clicks a button */
    onChange: (value: TlOnCameraValue) => void;
    /** Appends a red asterisk to the label */
    required?: boolean;
    disabled?: boolean;
    /**
     * Pass true to show the error border + message.
     * Typically set to `submitted && !value` in the parent.
     */
    error?: boolean;
}

const OPTIONS: TlOnCameraOption[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No',  value: 'No'  },
    { label: 'N/A', value: 'NA'  },
];

const TlOnCameraSelect: React.FC<TlOnCameraSelectProps> = ({
    label,
    value,
    onChange,
    required = false,
    disabled = false,
    error = false,
}) => {
    // PrimeReact v8 SelectButton onChange gives { originalEvent, value }
    const handleChange = useCallback(
        (e: { value: TlOnCameraValue }) => {
            // unselectable={false} prevents null, but guard for safety
            if (e.value !== null && e.value !== undefined) {
                onChange(e.value);
            }
        },
        [onChange],
    );

    const wrapperClass = useMemo(() => {
        const classes = ['tl-on-camera-select'];
        if (error)    classes.push('tl-on-camera-select--error');
        if (disabled) classes.push('tl-on-camera-select--disabled');
        return classes.join(' ');
    }, [error, disabled]);

    return (
        <div className={wrapperClass}>
            <span className="tl-on-camera-select__label">
                {label}
                {required && (
                    <span className="tl-on-camera-select__required" aria-hidden="true">
                        *
                    </span>
                )}
            </span>

            <SelectButton
                value={value}
                options={OPTIONS}
                optionLabel="label"
                optionValue="value"
                onChange={handleChange}
                disabled={disabled}
                unselectable={false}
                className="tl-on-camera-select__buttons"
                aria-label={label}
                aria-required={required}
            />

            {error && !value && (
                <small className="tl-on-camera-select__error-msg">This field is required.</small>
            )}
        </div>
    );
};

export default memo(TlOnCameraSelect);
